import { Accordion, Anchor, Box, Container, Group, Stack, Title } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Breadcrumb from '@components/breadcrumb';
import NextNav from '@components/next-nav';
import { VOYAGE_VIBE_RESULTS } from '@queries/voyage-vibe-state';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import GraphqlDumpData from '@components/graphql-dump-data';
import 'mantine-datatable/styles.css';
import 'mantine-datatable/styles.layer.css';
import classes from './vibe-results-table.module.css';

function VoyageVibeResultsTable() {
  const { clientId, tripProfileId = '' } = useParams();

  const { loading, error, data } = useQuery(VOYAGE_VIBE_RESULTS, {
    variables: {
      tripProfileId,
    },
    skip: !tripProfileId,
  });
  if (!tripProfileId) return null;
  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;
  const cWidth = 75;
  return (
    <div>
      <Breadcrumb pageStep={5} pb={30} />

      {data && data.findVoyageVibeResults[0] && (
        <Stack align="center">
          <Box className={classes.headerBar} w="98.5%" p={10}>
            <Group className="flex-row" justify="space-between">
              <Title order={2} className={classes.headerBarText}>
                Trip Profile: {data.findVoyageVibeResults[0].tripProfileTitle}
              </Title>
              <Title order={2} className={classes.headerBarText}>
                {data.findVoyageVibeResults[0].destinationName} ({data.findVoyageVibeResults[0].destinationNutsId}){' '}
              </Title>
            </Group>
          </Box>

          <Container fluid style={{ height: 'calc(70vh - 90px)' }}>
            <DataTable
              classNames={{
                header: classes.header,
              }}
              highlightOnHover
              withTableBorder
              withColumnBorders
              scrollAreaProps={{ type: 'always', scrollbarSize: 20 }}
              idAccessor="id"
              defaultColumnProps={{
                textAlign: 'right',
                noWrap: false,
              }}
              records={data.findVoyageVibeResults}
              columns={[
                {
                  accessor: 'nameLocal',
                  noWrap: true,
                  textAlign: 'left',
                  render: (record) => (
                    <Anchor
                      href={encodeURI(`https://www.openstreetmap.org/search?query=${record.nameLocal}`)}
                      target="_blank"
                    >
                      {record.nameLocal}
                    </Anchor>
                  ),
                },
                { accessor: 'type', textAlign: 'left' },
                {
                  accessor: 'wikidataId',
                  title: 'Wikidata ID',
                  render: (record) => (
                    <Anchor href={`https://www.wikidata.org/wiki/${record.wikidataId}`} target="_blank">
                      {record.wikidataId}
                    </Anchor>
                  ),
                },
                {
                  accessor: 'openStreetMapId',
                  title: 'OpenStreetMap ID',
                  render: (record) => (
                    <Anchor href={`https://www.openstreetmap.org/relation/${record.openStreetMapId}`} target="_blank">
                      {record.openStreetMapId}
                    </Anchor>
                  ),
                },
                // { accessor: 'mapboxId', title: 'Mapbox ID' },
                { accessor: 'attractionTypeRank', title: 'AtRnk', width: 70 },
                { accessor: 'physicalAdventure', title: 'PhysAdv', width: 85 },
                { accessor: 'interactivityLocals', title: 'IntrLocal', width: 85 },
                { accessor: 'learning', title: 'learn', width: 85 },
                { accessor: 'experienceDurationMin', title: 'Tmin', width: 60 },
                { accessor: 'experienceDurationMax', title: 'Tmax', width: 60 },
                { accessor: 'effortRating', title: 'Effort', width: 70 },
                { accessor: 'percentShopping', width: cWidth, title: 'pShop' },
                { accessor: 'percentCityTour', width: cWidth, title: 'pCTour' },
                { accessor: 'percentOutdoorAdventure', width: cWidth, title: 'pOutdoor' },
                { accessor: 'percentVisitMuseumGallery', width: cWidth, title: 'pMuseum' },
                { accessor: 'percentWalkingMeanderingLocally', width: cWidth, title: 'pWalk' },
                { accessor: 'percentAttendPerformanceFestival', width: cWidth, title: 'pFest' },
                { accessor: 'percentOutdoorRecreationExploration', width: cWidth, title: 'pExplor' },
                { accessor: 'resultShopping', width: cWidth, title: 'rShop' },
                { accessor: 'resultCityTour', width: cWidth, title: 'rCTour' },
                { accessor: 'resultOutdoorAdventure', width: cWidth, title: 'rOutdoor' },
                { accessor: 'resultVisitMuseumGallery', width: cWidth, title: 'rMuseum' },
                { accessor: 'resultWalkingMeanderingLocally', width: cWidth, title: 'rWalk' },
                { accessor: 'resultAttendPerformanceFestival', width: cWidth, title: 'rFest' },
                { accessor: 'resultOutdoorRecreationExploration', width: cWidth, title: 'rExplor' },
                { accessor: 'resultSum', width: cWidth, title: 'Sum' },
                { accessor: 'scoreActAttr', width: cWidth, title: 'Att*Act' },
                { accessor: 'rank', width: cWidth },
              ]}
            />
          </Container>
        </Stack>
      )}
      <NextNav saveVisible={false} next={`/clients/${clientId}/trip-profiles/${tripProfileId}/static-bowling-alley`} />
      <Accordion chevronPosition="left" variant="filled">
        <Accordion.Item value="customization">
          <Accordion.Control>Raw Data</Accordion.Control>
          <Accordion.Panel>
            <GraphqlDumpData data={data} />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}

export default VoyageVibeResultsTable;
